import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { brandLimitFor, effectivePlan, planEndsAt as planEndsAtOf, AGENCY_INCLUDED_BRANDS, type PlanName } from '@unipostudio/platform-constants';
import { useSubscription } from '@/shared/hooks/useBilling';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { useAuthStore } from '@/shared/stores/auth.store';
import { accountService, type AccountSummary } from '@/shared/services/account.service';
import { toPlanState, type BrandUsage, type PlanId } from '@/shared/services/billing.service';

export const ACCOUNTS_KEY = 'accounts';

/**
 * Plan state of the CURRENT brand's account (ADR-006): the plan belongs to the
 * account, so every brand of an account answers the same. There is no trial
 * and no read-only lapse (ADR-007): an account that is not paying is on the
 * free Starter plan, and what Starter does not include is refused with an
 * upgrade prompt (PLAN_REQUIRED / LIMIT_REACHED).
 */
export interface AccountState {
  /** False until the subscription (or the brand list carrying it) has loaded. */
  known: boolean;
  /** The plan in force: starter, classic (grandfathered), pro or agency. */
  plan: PlanId;
  /** On the free Starter plan. */
  free: boolean;
  /** On Classic, no longer sold: it keeps its features and price until it changes plan. */
  grandfathered: boolean;
  /**
   * When the paid plan in force falls back to Starter (a cancelled plan, an
   * admin's timed comp); null when it renews or the account is on Starter.
   */
  planEndsAt: Date | null;
  /** Brands used and allowed. */
  brands: BrandUsage;
  isAgency: boolean;
  /** The currency the subscription is billed in, when one is. */
  currency: string | null;
  /**
   * The signed-in person owns the account of the brand in view, so its plan
   * and checkout are theirs to change. True when the brand row does not say
   * (older API); the Billing tab cross-checks GET /accounts/me.
   */
  isAccountOwner: boolean;
}

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The account's plan state, from GET /subscriptions/me, falling back to the
 * account subscription carried on the current brand's /workspaces/me row so
 * the shell knows the state before billing has loaded.
 */
export function useAccountState(): AccountState {
  const { data: subscription } = useSubscription();
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const workspaces = useWorkspaceStore((s) => s.workspaces) ?? [];
  const userId = useAuthStore((s) => s.user?.id ?? null);

  const row = currentWorkspace?.subscription ?? null;
  const known = subscription !== undefined || row !== null;

  // The API's own answer when it sends one; otherwise decided here exactly as
  // the API decides it, from the brand row.
  const rowState = row
    ? { plan: row.plan, status: row.status, currentPeriodEnd: row.currentPeriodEnd ?? null, trialEndsAt: row.trialEndsAt ?? null }
    : null;
  const extraBrands = subscription?.extraBrands ?? row?.extraBrands ?? 0;

  // Before anything has loaded, nothing is gated: a paying account must not
  // see an upgrade prompt flash while its plan is on the way.
  const plan: PlanId = !known
    ? 'classic'
    : subscription
      ? subscription.effectivePlanId ??
        toPlanState(effectivePlan({ plan: subscription.planId.toUpperCase(), status: subscription.status, currentPeriodEnd: subscription.currentPeriodEnd }))
      : toPlanState(effectivePlan(rowState));
  const planEndsAt = subscription
    ? toDate(subscription.planEndsAt)
    : rowState
      ? planEndsAtOf(rowState)
      : null;

  const ownBrands = currentWorkspace?.accountId
    ? workspaces.filter((w) => w.accountId === currentWorkspace.accountId).length
    : workspaces.length;
  const planName = plan.toUpperCase() as PlanName;
  const brands: BrandUsage = subscription?.brands ?? {
    used: ownBrands,
    included: plan === 'agency' ? AGENCY_INCLUDED_BRANDS : brandLimitFor(planName),
    extra: plan === 'agency' ? extraBrands : 0,
    limit: brandLimitFor(planName, extraBrands),
  };

  return {
    known,
    plan,
    free: known && plan === 'starter',
    grandfathered: known && plan === 'classic',
    planEndsAt,
    brands,
    isAgency: plan === 'agency',
    currency: subscription?.currency ?? null,
    isAccountOwner: !currentWorkspace?.ownerId || !userId || currentWorkspace.ownerId === userId,
  };
}

/**
 * The account the signed-in person OWNS (GET /accounts/me). A new brand goes
 * into this account, whichever brand is current, so the add-brand sheet reads
 * its limits from here. Null for an invited member who owns none.
 */
export function useMyAccount(enabled = true): UseQueryResult<AccountSummary | null> {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: [ACCOUNTS_KEY, 'me'],
    queryFn: () => accountService.getMine(),
    enabled: enabled && isAuthenticated,
    staleTime: 60 * 1000,
  });
}
